import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";

// Critical pages - load immediately
import HomePage from "@/pages/HomePage";
import ComingSoonPage from "@/pages/ComingSoonPage";
// import TeacherVoucherPopup from "@/components/TeacherVoucherPopup"; — hidden per user request (Feb 2026)
import FloatingBookingButton from "@/components/FloatingBookingButton";
import ScrollBookingNudge from "@/components/ScrollBookingNudge";

// Lazy load other pages for better performance
const CompaniesPage = lazy(() => import("@/pages/CompaniesPage"));
const ClubsPage = lazy(() => import("@/pages/ClubsPage"));
const InsurancePage = lazy(() => import("@/pages/InsurancePage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const PhysioLabPage = lazy(() => import("@/pages/PhysioLabPage"));
const PhysioLabLibraryPage = lazy(() => import("@/pages/PhysioLabLibraryPage"));
const PhysioLabContentPage = lazy(() => import("@/pages/PhysioLabContentPage"));
const PhysioLabCategoryPage = lazy(() => import("@/pages/PhysioLabCategoryPage"));
const GiftVouchersPage = lazy(() => import("@/pages/GiftVouchersPage"));
const MonthlyProgramPage = lazy(() => import("@/pages/MonthlyProgramPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("@/pages/PaymentCancelPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfUsePage = lazy(() => import("@/pages/TermsOfUsePage"));
const CancelBookingPage = lazy(() => import("@/pages/CancelBookingPage"));
const ConditionPage = lazy(() => import("@/pages/ConditionPage"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ServicesOverviewPage = lazy(() => import("@/pages/ServicesOverviewPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const EquipmentRentalPage = lazy(() => import("@/pages/EquipmentRentalPage"));
const LocationPage = lazy(() => import("@/pages/LocationPage"));
const QuickHelpAdvicePage = lazy(() => import("@/pages/QuickHelpAdvicePage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#f87171] border-t-transparent"></div>
  </div>
);

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Floating booking button — visible on ALL public pages, hidden on admin
function GlobalFloatingButton() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin')) return null;
  if (pathname === '/coming-soon') return null;
  return (
    <>
      <FloatingBookingButton />
      <ScrollBookingNudge />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          {/* <TeacherVoucherPopup /> — hidden per user request (Feb 2026) */}
          <GlobalFloatingButton />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Live site - full homepage */}
              <Route path="/" element={<HomePage />} />
              
              {/* Public pages - all live */}
              <Route path="/podjetja" element={<CompaniesPage />} />
              <Route path="/klubi" element={<ClubsPage />} />
              <Route path="/zavarovalnice" element={<InsurancePage />} />
              <Route path="/darilni-boni" element={<GiftVouchersPage />} />
              <Route path="/mesecni-program" element={<MonthlyProgramPage />} />
              <Route path="/physiolab" element={<PhysioLabPage />} />
              <Route path="/physiolab/knjiznica" element={<PhysioLabLibraryPage />} />
              <Route path="/physiolab/:slug" element={<PhysioLabContentPage />} />
              
              {/* Legacy preview routes - redirect to main */}
              <Route path="/preview" element={<HomePage />} />
              <Route path="/preview/*" element={<HomePage />} />
              
              {/* SEO - Menu pages */}
              <Route path="/storitve-fizioterapija-ljubljana" element={<ServicesOverviewPage />} />
              <Route path="/cenik-fizioterapija-ljubljana" element={<PricingPage />} />
              <Route path="/fizioterapija-ljubljana-sasa-panic" element={<AboutPage />} />
              <Route path="/kontakt-fizioterapija-ljubljana" element={<ContactPage />} />
              <Route path="/izposoja-fizoterapevtske-opreme-ljubljana" element={<EquipmentRentalPage />} />
              <Route path="/fizioterapija-sasa-panic-lokacija" element={<LocationPage />} />
              
              {/* SEO - Condition pages */}
              <Route path="/kronicne-bolecine" element={<ConditionPage />} />
              <Route path="/pooperativna-rehabilitacija" element={<ConditionPage />} />
              <Route path="/sportne-poskodbe" element={<ConditionPage />} />
              <Route path="/preventiva-mobilnost" element={<ConditionPage />} />
              <Route path="/individualna-vadba" element={<ConditionPage />} />
              <Route path="/drza-svetovanje" element={<ConditionPage />} />
              
              {/* SEO - Service pages */}
              <Route path="/fizioterapija-ljubljana" element={<ServicePage />} />
              <Route path="/tecar-terapija-ljubljana" element={<ServicePage />} />
              <Route path="/laser-terapija-ljubljana" element={<ServicePage />} />
              <Route path="/dry-needling-ljubljana" element={<ServicePage />} />
              <Route path="/sportna-masaza-ljubljana" element={<ServicePage />} />
              <Route path="/masaza-nosecnic-ljubljana" element={<ServicePage />} />
              <Route path="/terapevtska-vadba-ljubljana" element={<ServicePage />} />
              <Route path="/kineziotaping-ljubljana" element={<ServicePage />} />

              {/* SEO - Nasveti & samopomoč (QuickHelp advice pages) */}
              <Route path="/bolecine-v-hrbtu-fizioterapija-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/bolecine-v-vratu-fizioterapija-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/bolecine-v-rami-fizioterapija-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/bolecine-v-kolenu-fizioterapija-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/zvin-gleznja-rehabilitacija-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/glavoboli-fizioterapija-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/terapija-za-nosecnice-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/sportne-poskodbe-prva-pomoc-ljubljana" element={<QuickHelpAdvicePage />} />
              {/* Phase 2 — 10 new SEO advice pages */}
              <Route path="/ishias-fizioterapija-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/hernija-diska-fizioterapija-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/teniski-komolec-fizioterapija-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/karpalni-tunel-sindrom-fizioterapija-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/bolecine-v-kolku-fizioterapija-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/petni-trn-plantarni-fasciitis-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/bolecine-v-zapestju-fizioterapija-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/zamrznjena-rama-frozen-shoulder-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/skolioza-fizioterapija-ljubljana" element={<QuickHelpAdvicePage />} />
              <Route path="/vrtoglavica-bppv-fizioterapija-ljubljana" element={<QuickHelpAdvicePage />} />
              
              {/* Always accessible */}
              <Route path="/coming-soon" element={<ComingSoonPage />} />
              <Route path="/zasebnost" element={<PrivacyPolicyPage />} />
              <Route path="/pogoji-uporabe" element={<TermsOfUsePage />} />
              <Route path="/cancel/:token" element={<CancelBookingPage />} />
              <Route path="/admin" element={<AdminLoginPage />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/payment/cancel" element={<PaymentCancelPage />} />

              {/* 301 Redirects - Stari Wix URL-ji */}
              <Route path="/o-meni" element={<Navigate to="/fizioterapija-ljubljana-sasa-panic" replace />} />
              <Route path="/kje-smo" element={<Navigate to="/fizioterapija-sasa-panic-lokacija" replace />} />
              <Route path="/fizioterapevtska-obravnava" element={<Navigate to="/fizioterapija-ljubljana" replace />} />
              <Route path="/service-page/fizioterapevtska-obravnava" element={<Navigate to="/fizioterapija-ljubljana" replace />} />
              <Route path="/service-page/posvet-s-fizioterapevtom" element={<Navigate to="/" replace />} />
              <Route path="/service-page/*" element={<Navigate to="/storitve-fizioterapija-ljubljana" replace />} />
              <Route path="/plans-pricing" element={<Navigate to="/mesecni-program" replace />} />
              <Route path="/shop" element={<Navigate to="/storitve-fizioterapija-ljubljana" replace />} />
              <Route path="/shop/*" element={<Navigate to="/storitve-fizioterapija-ljubljana" replace />} />
              <Route path="/account/*" element={<Navigate to="/" replace />} />
              <Route path="/cart" element={<Navigate to="/storitve-fizioterapija-ljubljana" replace />} />
              <Route path="/product-page/*" element={<Navigate to="/storitve-fizioterapija-ljubljana" replace />} />
              <Route path="/challenge-page/*" element={<Navigate to="/physiolab" replace />} />
              <Route path="/blog" element={<Navigate to="/physiolab" replace />} />
              <Route path="/blog/*" element={<Navigate to="/physiolab" replace />} />
              <Route path="/post/*" element={<Navigate to="/physiolab" replace />} />

              {/* Catch-all: unknown URLs → custom 404 with retention links */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </HelmetProvider>
  );
}

export default App;
